import * as types from '../actions/actionTypes/userActionTypes';
import { call, put, takeLatest, all, select } from 'redux-saga/effects';
import { Storage, API, graphqlOperation } from 'aws-amplify';
// Actions
import * as userActions from '../actions/userActions';
import * as notificationsActions from '../actions/errorHandlerActions';
import { getOrganizationData } from '../actions/organizationActions';
// Selectors
import { getUserData, getStudentDataReducer, getCourseIdReducer } from '../selectors/userSelectors';
import { getOrganizationDataReducer } from '../selectors/organizationSelector';
// Queries
import * as studentQueries from '../graphql/queries/studentQueries';
import * as queriesOrg from '../graphql/queries/organizationQueries';
import * as queries from '../graphql/queries';
// Mutations
import * as studentMutations from '../graphql/mutationsStudent';
import {
  updateCustomer,
  sendEmailSES,
  loginCognitoService,
  changePasswordCognitoService,
  getPasswordPoliciesCognitoServiceLambda,
  updateCognitoUserLambdaCall
} from '../graphql/queries/callsToLambdaFunctions';
// Another files
import GraphOp from '../sagas/common/GraphOp';
import { sendEmailProcess } from '../utils/sendEmailProcess';
import GetDefaultBlankImage from '../utils/getDefaultBlankImage';
import { envName } from '../../src/components/Common/const';

function* loginSagas(action) {
  const {
    email,
    mfaCode,
    setHasMfa,
    params,
    poolData,
    setErrorSignin,
    setLoadingSignin,
    handleErrorLogin,
    setOpenChangePasswordModal,
    setRedirectInformation
  } = action.payload;
  try {
    const [org] = yield all([select(getOrganizationDataReducer)]);
    const organizationId = org && org.id ? org.id : null;
    let response;
    let hasMfa = false;
    // If you are a team member and have the MFA activated we have to request it, since the userpool is shared.
    if (organizationId && email) {
      response = yield GraphOp(queriesOrg.stdGetTeamMemberWithEmailQuerie, {
        organizationId,
        email
      });
      if (
        response &&
        response.data &&
        response.data.listTeamMembers &&
        response.data.listTeamMembers.items &&
        response.data.listTeamMembers.items[0] &&
        response.data.listTeamMembers.items[0].mfaEnabled
      ) {
        hasMfa = true;
      }
    }
    if (hasMfa && !mfaCode) {
      if (setHasMfa) setHasMfa(true);
      if (setLoadingSignin) setLoadingSignin(false);
    } else {
      // Pool data to get user
      // Params from loginView
      response = yield GraphOp(loginCognitoService, { params, poolData });
      if (!response.data.loginCognitoService) {
        if (setLoadingSignin) setLoadingSignin(false);
        throw new Error('Error logging in, please try again. Or contact with support.');
      } else {
        const responseParsed = JSON.parse(response.data.loginCognitoService);
        const userResponse = responseParsed?.userResponse;
        const result = responseParsed?.result;
        const showModal = responseParsed?.showModal;
        yield put(getOrganizationData({}));
        // if show modal is the first time the user logs in or reset the password workflow
        // the redirect function is called from the modal
        if (showModal && setOpenChangePasswordModal) {
          if (setLoadingSignin) setLoadingSignin(false);
          setOpenChangePasswordModal(showModal);
          setRedirectInformation({ result, userResponse });
        } else {
          setRedirectInformation({ result, userResponse, redirect: true });
        }
      }
    }
  } catch (error) {
    if (setLoadingSignin) setLoadingSignin(false);
    if (error?.errors?.length > 0) {
      const errorMessage = JSON.parse(error.errors[0].message);
      if (errorMessage.name && handleErrorLogin) {
        handleErrorLogin(errorMessage.name);
      }
      if (errorMessage?.name === 'NotAuthorizedException' && setErrorSignin) {
        return setErrorSignin('Invalid email or password');
      }
    }
    yield put(notificationsActions.handleCatchError(error, 'loginSagas-Student'));
    if (setErrorSignin) {
      setErrorSignin('Error logging in, please try again. Or contact with support.');
    }
  }
}

function* getPasswordPoliciesSagas(action) {
  const { params, setPasswordPolicy } = action.payload;
  let policy = {
    MinimumLength: 8,
    RequireUppercase: true,
    RequireLowercase: true,
    NoWhiteSpaces: true
  };
  try {
    const response = yield GraphOp(getPasswordPoliciesCognitoServiceLambda, { params });
    if (response && response.data && response.data.getPasswordPoliciesCognitoService) {
      const parsed = JSON.parse(response.data.getPasswordPoliciesCognitoService);
      if (parsed) policy = { ...parsed, NoWhiteSpaces: true };
    }
  } catch (error) {
    yield put(notificationsActions.handleCatchError(error, 'getPasswordPoliciesSagas'));
  } finally {
    sessionStorage.setItem('passwordPolicy', JSON.stringify(policy));
    setPasswordPolicy(policy);
  }
}

function* changePasswordSagas(action) {
  const {
    userParams,
    userEmail,
    setLoadingPasswordChange,
    setErrorPasswordChange,
    setErrorValidationCode,
    setSuccessPasswordChange,
    setErrorSignin,
    sendConfirmationPasswordEmail,
    setVerificationCodeSent,
    setForgetPasswordMode,
    setLoadingValidationCode
  } = action.payload;
  try {
    if (!userParams) {
      throw new Error('Error changing password, missing user params.');
    }
    const [org] = yield all([select(getOrganizationDataReducer)]);
    const organizationId = org && org.id ? org.id : null;
    if (userEmail && organizationId) {
      // Get the user name
      let responseId = yield GraphOp(studentQueries.stdListStudentsWithThisEmailQuerie, {
        organizationId,
        studentEmail: userEmail
      });
      if (responseId && responseId.data && responseId.data.listStudents) {
        if (
          responseId.data.listStudents.items &&
          responseId.data.listStudents.items[0] &&
          responseId.data.listStudents.items[0].id
        ) {
          userParams.Username = responseId.data.listStudents.items[0].id;
        }
        let nextToken = responseId.data.listStudents.nextToken
          ? responseId.data.listStudents.nextToken
          : null;
        while (nextToken) {
          if (userParams.Username) nextToken = null;
          else {
            // If has nextToken it means that are more users to get
            responseId = yield GraphOp(studentQueries.stdListStudentsWithThisEmailQuerie, {
              organizationId,
              studentEmail: userEmail,
              nextToken
            });
            if (responseId && responseId.data && responseId.data.listStudents) {
              if (
                responseId.data.listStudents.items &&
                responseId.data.listStudents.items[0] &&
                responseId.data.listStudents.items[0].id
              ) {
                userParams.Username = responseId.data.listStudents.items[0].id;
              }
              nextToken = responseId.data.listStudents.nextToken
                ? responseId.data.listStudents.nextToken
                : null;
            }
          }
        }
      }
    }
    const response = yield GraphOp(changePasswordCognitoService, { userParams });
    if (!response.data.changePasswordCognitoService) {
      throw new Error('Error changing password, please try again. Or contact with support.');
    }
    const responseParsed = JSON.parse(response.data.changePasswordCognitoService);
    if (responseParsed) {
      setLoadingPasswordChange(false);
      setErrorPasswordChange(null);
      setErrorValidationCode(null);
      setErrorSignin(null);
      setSuccessPasswordChange(true);
      sendConfirmationPasswordEmail();
      setVerificationCodeSent(false);
      setForgetPasswordMode(false);
    } else {
      setLoadingPasswordChange(false);
      setSuccessPasswordChange(false);
      setLoadingValidationCode(false);
    }
  } catch (error) {
    yield put(notificationsActions.handleCatchError(error, 'changePasswordSagas-Student'));
    setLoadingPasswordChange(false);
    setSuccessPasswordChange(false);
    if (error.message) {
      setErrorPasswordChange(error.message);
    } else {
      setErrorPasswordChange('Error changing password, please try again. Or contact with support.');
    }
    throw new Error('Error changing password, please try again. Or contact with support.');
  }
}

function* sendEmailSagas(action) {
  const mailParams = sendEmailProcess(action.payload);
  // destructure params if for forget password workflow
  const {
    setForgetPasswordCode,
    vertificationCode,
    setVerificationCodeSent,
    setErrorValidationCode,
    setLoadingValidationCode,
    setLoadingButton
  } = action.payload;
  // mailParams is a stringified JSON object containing the mail parameters
  // execute lambda function to send the email and return the result
  // use try/catch to handle errors
  try {
    const data = yield GraphOp(sendEmailSES, {
      mailParams
    });
    // If verification code is the forgot password workflow
    if (vertificationCode) {
      setForgetPasswordCode(vertificationCode);
      setVerificationCodeSent(true);
      setLoadingValidationCode(false);
      setErrorValidationCode(null);
      setLoadingButton(false);
    }
    return data;
  } catch (err) {
    yield put(notificationsActions.handleCatchError(err, 'sendEmailSagas'));
    yield put(
      notificationsActions.setNotification({
        message: 'Problem Sending email, please try again or contact with support.',
        severity: 'error'
      })
    );
    // If verification code is the forgot password workflow
    if (vertificationCode) {
      setLoadingValidationCode(false);
      setErrorValidationCode(err.message);
      setLoadingButton(false);
    }
    return err;
  }
}

function* findMylaunchDarklyAccessSagas() {
  try {
    if (envName) {
      const response = yield GraphOp(queries.getLaunchDarklyAccess, { envName });
      if (response && response.data && response.data.getLaunchDarklyAccess) {
        yield all([put(userActions.setMyLaunchDatklyAccess(response.data.getLaunchDarklyAccess))]);
      }
    }
  } catch (err) {
    yield yield put(notificationsActions.handleCatchError(err, 'GetMylauchDarklyAccessSagas'));
  }
}

function* getUserLogoSagas() {
  try {
    const [userData] = yield all([select(getUserData), put(userActions.loadingUserLogo(true))]);
    const userId = userData && userData.username ? userData.username : null;
    if (userId) {
      const filePath = `${userId}/profileImage`;
      let avatarUrl = yield call([Storage, 'get'], filePath);
      const photoResponse = yield fetch(avatarUrl, { method: 'GET' });
      if (photoResponse && photoResponse.status !== 200) {
        const defaultImage = yield call(GetDefaultBlankImage);
        yield call([Storage, 'put'], filePath, defaultImage);
        avatarUrl = yield call([Storage, 'get'], filePath);
      }
      yield all([put(userActions.setUserLogo(avatarUrl))]);
    }
    yield all([put(userActions.loadingUserLogo(false))]);
  } catch (err) {
    yield all([put(userActions.loadingUserLogo(false)), yield put(userActions.setUserLogo(null))]);
    yield put(notificationsActions.handleCatchError(err, 'getUserLogoSagas'));
  }
}

function* uploadUserLogoSagas(action) {
  try {
    const [userData] = yield all([select(getUserData), put(userActions.loadingUserLogo(true))]);
    const userId = userData && userData.username ? userData.username : null;
    if (userId) {
      const filePath = `${userId}/profileImage`;
      const image = action.value;
      yield call([Storage, 'put'], filePath, image);
      yield all([put(userActions.getUserLogo())]);
    }
    yield put(userActions.loadingUserLogo(false));
  } catch (err) {
    yield all([
      put(
        notificationsActions.setNotification({
          message: 'Unable to complete request. Please contact us for support.',
          severity: 'error'
        })
      ),
      put(userActions.loadingUserLogo(false))
    ]);
    yield put(notificationsActions.handleCatchError(err, 'uploadUserLogoSagas'));
  }
}

function* getStudentDataSagas(action) {
  try {
    const { studentId, organizationId } = action.value;
    if (studentId && organizationId) {
      const response = yield GraphOp(studentQueries.stdGetStudentDataQuerie, {
        studentId,
        organizationId
      });
      const studentData =
        response && response.data && response.data.getStudent ? response.data.getStudent : null;
      if (studentData && studentData.id) {
        window.analytics.identify(studentData.id, {
          name: studentData.name,
          email: studentData.email
        });
        yield put(userActions.setStudentData(studentData));
      }
    }
  } catch (err) {
    yield put(notificationsActions.handleCatchError(err, 'getStudentDataSagas'));
  }
}

function* updateStudentEmailSagas(action) {
  const { email, organizationId, studentId, stripeId, loading, finishAction } = action.value;
  if (loading) loading(true);
  try {
    const [studentData, organizationData] = yield all([
      select(getStudentDataReducer),
      select(getOrganizationDataReducer)
    ]);
    // Update on cognito
    const lambdaParams = {};
    lambdaParams.operation = 'EditEmail';
    lambdaParams.UserPoolId =
      localStorage && localStorage.UserPoolId ? JSON.parse(localStorage.getItem('UserPoolId')) : '';
    lambdaParams.Username = studentId;
    lambdaParams.newEmail = email;
    lambdaParams.attName = 'email';
    const response = yield GraphOp(updateCognitoUserLambdaCall, {
      params: JSON.stringify(lambdaParams)
    });
    if (
      !(
        response &&
        response.data &&
        response.data.updateCognitoUser &&
        response.data.updateCognitoUser &&
        response.data.updateCognitoUser.includes('httpStatusCode":200')
      )
    ) {
      throw new Error('Lambda response');
    }
    // Update on dynamo
    const studentResponse = yield GraphOp(studentMutations.updateStudentEmail, {
      studentId,
      organizationId,
      email
    });
    yield put(userActions.setStudentData(studentResponse.data.updateStudent));
    // Update on reducer
    const userData = yield select(getUserData);
    userData.attributes.email = email;
    userData.attributes.email_verified = false;
    yield put(userActions.setUser(userData));
    // Update the email on stripe
    let organizationCustomer;
    if (
      studentData &&
      studentData.organizationCustomers &&
      studentData.organizationCustomers.length > 0 &&
      organizationData
    ) {
      organizationCustomer = studentData.organizationCustomers.find(
        oc => oc.organizationID === organizationData.id
      );
    }
    if (finishAction) finishAction();
    if (organizationCustomer && organizationCustomer.customerID) {
      const billingInfo = {};
      billingInfo.email = email;
      yield GraphOp(updateCustomer, {
        customerId: organizationCustomer.customerID,
        stripeAccount: stripeId,
        billingInfo: JSON.stringify(billingInfo),
        envName: envName
      });
    } else {
      yield put(
        notificationsActions.setNotification({
          message: 'Unable to complete request. Please contact us for support.',
          severity: 'error'
        })
      );
    }
  } catch (err) {
    yield put(notificationsActions.handleCatchError(err, 'updateStudentEmailSagas'));
  } finally {
    if (loading) loading(false);
  }
}

function* updateStudentNameSagas(action) {
  let { setNameToStripe, setLoading } = action.value;
  try {
    const { name, organizationId, studentId, stripeId } = action.value;
    //first update the student Data
    let studentResponse = yield GraphOp(studentMutations.updateStudentName, {
      studentId: studentId,
      organizationId: organizationId,
      name: name
    });
    if (studentResponse && studentResponse.data && studentResponse.data.updateStudent) {
      yield put(userActions.setStudentData(studentResponse.data.updateStudent));
      studentResponse = studentResponse.data.updateStudent;
    }

    //update the name on stripe
    const [studentData, organizationData] = yield all([
      select(getStudentDataReducer),
      select(getOrganizationDataReducer)
    ]);
    let organizationCustomer;

    if (
      studentData &&
      studentData.organizationCustomers &&
      studentData.organizationCustomers.length > 0 &&
      organizationData
    ) {
      organizationCustomer = studentData.organizationCustomers.find(
        oc => oc.organizationID === organizationData.id
      );
    }

    if (organizationCustomer && organizationCustomer.customerID) {
      let billingInfo = {};
      billingInfo.name = name;

      let response = yield GraphOp(updateCustomer, {
        customerId: organizationCustomer.customerID,
        stripeAccount: stripeId,
        billingInfo: JSON.stringify(billingInfo),
        envName: envName
      });

      response = JSON.parse(response.data.updateCustomer);

      if (response && response.id) {
        //update name successful
        yield put(
          notificationsActions.setNotification({
            message: 'Name updated correctly',
            severity: 'success'
          })
        );
      } else {
        yield put(notificationsActions.handleCatchError('Error updating name'));
      }
    } else {
      if (studentResponse && studentResponse.id) {
        yield put(
          notificationsActions.setNotification({
            message: 'Name updated correctly',
            severity: 'success'
          })
        );
      } else {
        yield put(
          notificationsActions.setNotification({
            message: 'Unable to complete request. Please contact us for support.',
            severity: 'error'
          })
        );
      }
    }
    if (setNameToStripe) setNameToStripe(false);
    if (setLoading) setLoading(false);
  } catch (err) {
    if (setNameToStripe) setNameToStripe(false);
    if (setLoading) setLoading(false);
    yield put(notificationsActions.handleCatchError(err, 'updateStudentNameSagas'));
  }
}

function* handlePreviewUserQuestionsSagas() {
  try {
    const [studentData, courseId, organizationData] = yield all([
      select(getStudentDataReducer),
      select(getCourseIdReducer),
      select(getOrganizationDataReducer)
    ]);
    const organizationID = organizationData && organizationData.id ? organizationData.id : null;
    const id = studentData && studentData.id ? studentData.id : null;
    if (studentData && studentData.organizationCustomers && organizationID && id) {
      // First of all we check for the orgCustomer of this org.
      let orgCustomers = studentData.organizationCustomers.find(
        item => item.organizationID === organizationData.id
      );
      if (orgCustomers) {
        if (orgCustomers.subscriptions && orgCustomers.subscriptions.length > 0) {
          orgCustomers.subscriptions.forEach(sub => {
            if (sub && sub.courseID && sub.courseID === courseId && sub.active === true) {
              // We take off one question from the preview.
              // If we reach the zero questions we change the subscription state .
              sub.questionsLeft--;
              if (sub.questionsLeft === 0) sub.active = false;
            }
          });
        }
        const studentDataUpdated = yield call(
          [API, 'graphql'],
          graphqlOperation(studentMutations.updateOrganizationCustomer, {
            organizationID,
            id,
            organizationCustomers: orgCustomers
          })
        );
        yield put(userActions.setStudentData(studentDataUpdated.data.updateStudent));
      }
    }
  } catch (err) {
    yield put(notificationsActions.handleCatchError(err, 'handlePreviewUserQuestionsSagas'));
  }
}

function* deleteStudentSagas(action) {
  try {
    const { organizationId, id, userPoolId, email } = action.payload;
    let deleteFromCognito = false;
    // Delete student from database
    yield GraphOp(studentMutations.deleteStudent, {
      organizationId,
      id
    });
    //We ask if have a team member account
    const isTeamMember = yield GraphOp(queriesOrg.stdIsTeamMemberQuerie, {
      userId: id,
      organizationId
    });
    //Delete the student's cognito account if the student is not a team person
    if (!isTeamMember.data.getTeamMember) deleteFromCognito = true;
    if (deleteFromCognito) {
      const lambdaParams = {};
      lambdaParams.operation = 'DeleteUser';
      lambdaParams.removeToGroup = 'USER_ROLE_STUDENT';
      lambdaParams.UserPoolId = userPoolId;
      lambdaParams.Username = id;
      lambdaParams.email = email;
      yield GraphOp(updateCognitoUserLambdaCall, {
        params: JSON.stringify(lambdaParams)
      });
    }
  } catch (err) {
    yield put(notificationsActions.handleCatchError(err, 'deleteStudentSagas'));
  }
}

function* checkEmailRegisteredSagas(action) {
  const { email, setUserRegistered, setLoading, setNameUserRegistered } = action.payload;
  try {
    if (setLoading) setLoading(true);
    const [organizationData] = yield all([select(getOrganizationDataReducer)]);
    const organizationId = organizationData && organizationData.id ? organizationData.id : null;
    let allTheStudents = [];
    if (organizationId) {
      let response = yield GraphOp(studentQueries.stdGetStudentByEmailQuerie, {
        studentEmail: email,
        organizationId: organizationId
      });
      if (
        response &&
        response.data &&
        response.data.getOrganization &&
        response.data.getOrganization.students
      ) {
        if (
          response.data.getOrganization.students.items &&
          response.data.getOrganization.students.items.length > 0
        ) {
          allTheStudents = [...response.data.getOrganization.students.items];
        }
        let nextToken = response.data.getOrganization.students.nextToken
          ? response.data.getOrganization.students.nextToken
          : null;
        while (nextToken) {
          if (allTheStudents.length > 0) nextToken = null;
          else {
            // If has nextToken it means that are more users to get
            response = yield GraphOp(studentQueries.stdGetStudentByEmailQuerie, {
              studentEmail: email,
              organizationId,
              nextToken
            });
            if (
              response &&
              response.data &&
              response.data.getOrganization &&
              response.data.getOrganization.students
            ) {
              if (
                response.data.getOrganization.students.items &&
                response.data.getOrganization.students.items.length > 0
              ) {
                allTheStudents = allTheStudents.concat(
                  response.data.getOrganization.students.items
                );
              }
              nextToken = response.data.getOrganization.students.nextToken
                ? response.data.getOrganization.students.nextToken
                : null;
            }
          }
        }
      }
    }
    if (allTheStudents && allTheStudents.length > 0) {
      if (setUserRegistered) setUserRegistered(true);
      if (setNameUserRegistered) setNameUserRegistered(allTheStudents[0].name);
      yield all([
        put(userActions.setStudentData(allTheStudents[0])),
        put(
          notificationsActions.setNotification({
            message:
              'This email address is already associated with an account. Please try logging in.',
            severity: 'error'
          })
        )
      ]);
    } else {
      if (setUserRegistered) setUserRegistered(false);
      if (setNameUserRegistered) setNameUserRegistered(null);
      yield put(userActions.setStudentData(null));
    }
    if (setLoading) setLoading(false);
  } catch (err) {
    yield put(notificationsActions.handleCatchError(err, 'checkEmailRegisteredSagas'));
    if (setLoading) setLoading(false);
    if (setUserRegistered) setUserRegistered(false);
    if (setNameUserRegistered) setNameUserRegistered(null);
  }
}

function* updateUserInCognitoSagas(action) {
  const {
    // Sagas handle
    operation,
    loading,
    finishAction,
    // User handle
    userId,
    oldPassword,
    newPassword
  } = action.payload;
  if (loading) loading(true);
  let sucessMsg;
  // Const
  const UserPoolId =
    localStorage && localStorage.UserPoolId ? JSON.parse(localStorage.getItem('UserPoolId')) : '';
  const refreshToken =
    localStorage && localStorage.RefreshToken
      ? JSON.parse(localStorage.getItem('RefreshToken'))
      : '';
  const ClientId =
    localStorage && localStorage.ClientId ? JSON.parse(localStorage.getItem('ClientId')) : '';
  try {
    const lambdaParams = {};
    lambdaParams.operation = operation;
    lambdaParams.UserPoolId = UserPoolId;
    if (userId) lambdaParams.Username = userId;
    if (operation === 'ChangePassword') {
      sucessMsg = 'Password successfully changed!';
      lambdaParams.PreviousPassword = oldPassword;
      lambdaParams.ProposedPassword = newPassword;
      lambdaParams.refreshToken = refreshToken;
      lambdaParams.ClientId = ClientId;
    }
    if (operation === 'ValidateResfreshToken') {
      lambdaParams.refreshToken = refreshToken;
      lambdaParams.ClientId = ClientId;
    }
    const response = yield GraphOp(updateCognitoUserLambdaCall, {
      params: JSON.stringify(lambdaParams)
    });
    if (
      response &&
      response.data &&
      response.data.updateCognitoUser &&
      response.data.updateCognitoUser &&
      response.data.updateCognitoUser.includes('httpStatusCode":200')
    ) {
      if (sucessMsg) {
        yield put(
          notificationsActions.setNotification({
            message: sucessMsg,
            severity: 'success'
          })
        );
      }
      if (finishAction) finishAction();
    } else {
      yield put(
        notificationsActions.setNotification({
          message: 'Unable to complete request. Please contact us for support.',
          severity: 'error'
        })
      );
    }
  } catch (err) {
    if (operation === 'ValidateResfreshToken') {
      localStorage.removeItem('token_timestamp');
    } else {
      let snackbarMsg = 'Unable to complete request. Please contact us for support.';
      // Handle error
      if (err && err.errors && err.errors[0] && err.errors[0].message) {
        const errorMessage = JSON.parse(err.errors[0].message);
        if (errorMessage && errorMessage.name) {
          if (errorMessage.name === 'NotAuthorizedException') {
            snackbarMsg = 'The password entered is not correct, Please try again.';
          }
          if (errorMessage.name === 'LimitExceededException') {
            snackbarMsg = 'You have reached the limit of attempts. Try again later';
          }
        }
      }
      yield put(
        notificationsActions.setNotification({
          message: snackbarMsg,
          severity: 'error'
        })
      );
      if (snackbarMsg !== 'Unable to complete request. Please contact us for support.') {
        yield put(notificationsActions.handleCatchError(err, 'updateUserInCognitoSagas'));
      }
    }
  } finally {
    if (loading) loading(false);
  }
}

// Watchers
function* loginWatcher() {
  yield takeLatest(types.LOGIN, loginSagas);
}
function* getPasswordPoliciesWatcher() {
  yield takeLatest(types.GET_PASSWORD_POLICIES, getPasswordPoliciesSagas);
}
function* changePasswordWatcher() {
  yield takeLatest(types.CHANGE_PASSWORD, changePasswordSagas);
}
function* sendEmailWatcher() {
  yield takeLatest(types.SEND_EMAIL, sendEmailSagas);
}
function* findMylaunchDarklyAccessWatcher() {
  yield takeLatest(types.FIND_MY_LAUNCH_DARKLY_ACCESS, findMylaunchDarklyAccessSagas);
}
function* getUserLogoWatcher() {
  yield takeLatest(types.GET_USER_LOGO, getUserLogoSagas);
}
function* uploadUserLogoWatcher() {
  yield takeLatest(types.UPLOAD_USER_LOGO, uploadUserLogoSagas);
}
function* getStudentDataWatcher() {
  yield takeLatest(types.GET_STUDENT_DATA, getStudentDataSagas);
}
function* updateStudentEmailWatcher() {
  yield takeLatest(types.UPDATE_STUDENT_EMAIL, updateStudentEmailSagas);
}
function* updateStudentNameWatcher() {
  yield takeLatest(types.UPDATE_STUDENT_NAME, updateStudentNameSagas);
}
function* deleteStudentWatcher() {
  yield takeLatest(types.DELETE_STUDENT, deleteStudentSagas);
}
function* checkEmailRegisteredWatcher() {
  yield takeLatest(types.CHECK_EMAIL_REGISTERED, checkEmailRegisteredSagas);
}
function* handlePreviewUserQuestionWatcher() {
  yield takeLatest(types.HANDLE_PREVIEW_USER_QUESITONS, handlePreviewUserQuestionsSagas);
}
function* updateUserInCognitoWatcher() {
  yield takeLatest(types.UPDATE_USER_IN_COGNITO, updateUserInCognitoSagas);
}

// Exports the sagas
export default function* sagas() {
  yield all([
    loginWatcher(),
    getPasswordPoliciesWatcher(),
    changePasswordWatcher(),
    sendEmailWatcher(),
    getUserLogoWatcher(),
    uploadUserLogoWatcher(),
    getStudentDataWatcher(),
    updateStudentEmailWatcher(),
    updateStudentNameWatcher(),
    deleteStudentWatcher(),
    checkEmailRegisteredWatcher(),
    handlePreviewUserQuestionWatcher(),
    findMylaunchDarklyAccessWatcher(),
    updateUserInCognitoWatcher()
  ]);
}
